import { useTranslation } from 'react-i18next'
import { Avatar, cn } from '@ludus/ui'
import { managerDisplayName, type InternalStatus, type Lead, type Manager } from '@ludus/core'
import { FunnelPill } from '@/shared/components/FunnelPill.tsx'
import { funnelChannelFor } from '@/shared/utils/funnels.ts'
import { formatPrice } from '../utils/format.ts'

interface ValueSummaryStripProps {
  lead: Lead
  stage: InternalStatus | null
  owner: Manager | null
  showValue: boolean
}

export function ValueSummaryStrip({ lead, stage, owner, showValue }: ValueSummaryStripProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-4 sm:gap-x-10">
      {/* Accent metric: deal value gets the largest type so the eye lands on it first. */}
      {showValue && (
        <Cell label={t('pages.leads.detail.summary.value', { defaultValue: 'Deal value' })}>
          {lead.price !== null ? (
            <span className="text-xl font-bold tabular-nums tracking-tight text-foreground sm:text-2xl">
              {formatPrice(lead.price)}
            </span>
          ) : (
            <span className="text-sm text-muted">
              {t('pages.leads.detail.summary.noValue', { defaultValue: 'No value set' })}
            </span>
          )}
        </Cell>
      )}
      <Cell label={t('pages.leads.detail.summary.stage', { defaultValue: 'Stage' })}>
        <StageBadge stage={stage} fallbackId={lead.statusId} />
      </Cell>
      <Cell label={t('pages.leads.detail.summary.owner', { defaultValue: 'Owner' })}>
        {owner ? (
          <span className="inline-flex items-center gap-2">
            <Avatar
              size="sm"
              seed={owner.id}
              initials={initials(owner)}
              aria-label={managerDisplayName(owner)}
            />
            <span className="truncate text-sm text-foreground">{managerDisplayName(owner)}</span>
          </span>
        ) : (
          <span className="text-sm text-muted">
            {t('pages.leads.detail.fields.unassigned', { defaultValue: 'Unassigned' })}
          </span>
        )}
      </Cell>
      {/* Secondary metric: source pushed right with `ml-auto`, lighter weight so the strip
          reads as left-anchored DEAL VALUE / STAGE / OWNER + a quieter rightmost data point.
          Raw seed/CRM keys (`google_ads`) are humanized via `funnelChannelFor` and rendered
          with the same `FunnelPill` abbr that the pipelines table uses, so source identity
          is visually consistent across the app. */}
      <Cell
        label={t('pages.leads.detail.summary.source', { defaultValue: 'Source' })}
        className="ml-auto"
      >
        {lead.funnel ? (
          (() => {
            const f = funnelChannelFor(lead.funnel)
            return (
              <span className="inline-flex items-center gap-2">
                <FunnelPill abbr={f.abbr} label={f.label} />
                <span className="truncate text-sm text-foreground/85">{f.label}</span>
              </span>
            )
          })()
        ) : (
          <span className="text-sm text-muted">
            {t('pages.leads.detail.summary.noSource', { defaultValue: '—' })}
          </span>
        )}
      </Cell>
    </div>
  )
}

/**
 * Pill rendering of the current stage. Visually mirrors the dot used on the
 * stage-progress track so the eye links "this is where the deal is right
 * now" between the two surfaces. Falls back to raw id if the stage record
 * isn't loaded.
 */
function StageBadge({ stage, fallbackId }: { stage: InternalStatus | null; fallbackId: string }) {
  if (!stage) {
    return <code className="font-mono text-xs text-muted">{fallbackId}</code>
  }
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/35 bg-primary/15 px-2.5 py-1 text-sm font-medium text-foreground">
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_6px_rgba(99,102,241,0.6)]"
      />
      <span className="truncate">{stage.name}</span>
    </span>
  )
}

function Cell({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

function initials(m: Manager): string {
  return [m.firstName, m.lastName]
    .map((s) => s.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .join('')
}
